import { isPlatformBrowser } from '@angular/common';
import { filter, take } from 'rxjs';

import { AppConfigService } from '../app-config.service';
import { redactNetworkBody } from './openreplay-network-sanitizer';
import { environment } from '@env/environment';
import { IPSAuthenticationService, OpenReplayService } from '@ips/common-angular';
import Tracker from '@openreplay/tracker';
import { OAuthService } from 'angular-oauth2-oidc';

const MAX_TOKEN_WAIT_ATTEMPTS = 150;
const TOKEN_WAIT_DELAY_MS = 200;

function resolveIngestPoint(ingestPoint: string): string {
	if (ingestPoint.startsWith('http')) {
		return ingestPoint;
	}
	return `${window.location.origin}${ingestPoint}`;
}

function createTracker(projectKey: string, ingestPoint: string): Tracker {
	return new Tracker({
		projectKey,
		ingestPoint,
		// Disable string dictionary compression to avoid decode errors in replay
		disableStringDict: true,
		// Value 3 = InlineCssMode.PlainFetched (enum not exported from tracker types)
		// Requires BEACON_SIZE_LIMIT >= 2000000 on the OpenReplay http service.
		inlineCss: 0,
		network: {
			sessionTokenHeader: false,
			failuresOnly: false,
			ignoreHeaders: ['Cookie', 'Set-Cookie', 'Authorization'],
			captureInIframes: true,
			capturePayload: true,
			sanitizer: (data) => {
				data.request.body = redactNetworkBody(data.request.body);
				data.response.body = redactNetworkBody(data.response.body);
				return data;
			}
		}
	});
}

function waitForToken(oauthService: OAuthService, attempts = 0): Promise<string> {
	if (attempts >= MAX_TOKEN_WAIT_ATTEMPTS) {
		return Promise.reject(new Error('OpenReplay: giving up waiting for auth token'));
	}

	const token = oauthService.getAccessToken();
	if (token) {
		return Promise.resolve(token);
	}

	return new Promise((resolve) => setTimeout(resolve, TOKEN_WAIT_DELAY_MS)).then(() => waitForToken(oauthService, attempts + 1));
}

function setUserIdentity(authenticationService: IPSAuthenticationService, openReplayService: OpenReplayService): void {
	authenticationService.currentUser
		.pipe(
			filter((user) => !!user?.authenticated && !!(user.email || user.username)),
			take(1)
		)
		.subscribe((user) => {
			const userEmail = user.email || user.username;
			openReplayService.setUserData({
				id: userEmail ?? user.id ?? 'unknown',
				email: user.email,
				name: user.displayName
			});
		});
}

export function initOpenReplay(
	openReplayService: OpenReplayService,
	appConfigService: AppConfigService,
	authenticationService: IPSAuthenticationService,
	oauthService: OAuthService,
	platformId: object
) {
	return () => {
		if (!isPlatformBrowser(platformId)) {
			return Promise.resolve();
		}

		return appConfigService.loadConfig().then(() => {
			const config = appConfigService.getConfig();
			const projectKey = config?.openReplayProjectKey;
			if (config?.openReplayEnabled === 'true' && projectKey) {
				// Wait for auth token before initializing OpenReplay.
				// Runs asynchronously — does not block APP_INITIALIZER.
				void waitForToken(oauthService).then(() => {
					const ingestPoint = resolveIngestPoint(environment.openReplayIngestPoint);
					const tracker = createTracker(projectKey, ingestPoint);
					openReplayService.tracker = tracker;
					void openReplayService.start();
					setUserIdentity(authenticationService, openReplayService);
				});
			}
		});
	};
}